import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export type DateRangePickerMode = "date" | "month" | "quarter";

interface DateRangeValue {
  start: Date | null;
  end: Date | null;
}

interface DateRangePickerProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  mode: DateRangePickerMode;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  startPlaceholder?: string;
  endPlaceholder?: string;
  size?: "sm";
}

const getDatePickerProps = (mode: DateRangePickerMode) => {
  switch (mode) {
    case "month":
      return {
        showMonthYearPicker: true,
        dateFormat: "MMM yyyy",
      };
    case "quarter":
      return {
        showQuarterYearPicker: true,
        dateFormat: "yyyy, QQQ",
      };
    case "date":
    default:
      return {
        dateFormat: "dd/MM/yyyy",
      };
  }
};

const DateRangePicker = ({
  value,
  onChange,
  mode,
  minDate,
  maxDate,
  disabled = false,
  startPlaceholder = "Start date",
  endPlaceholder = "End date",
  size,
}: DateRangePickerProps) => {
  const sizeClass = size === "sm" ? " form-control-sm" : "";
  const [editStart, setEditStart] = useState<Date | null>(value.start);
  const [editEnd, setEditEnd] = useState<Date | null>(value.end);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (!hasChanges) {
      setEditStart(value.start);
      setEditEnd(value.end);
    }
  }, [value.start, value.end, hasChanges]);

  const modeProps = getDatePickerProps(mode);

  const isValid =
    !editStart || !editEnd || editStart.getTime() <= editEnd.getTime();

  const handleStartChange = (date: Date | null) => {
    setEditStart(date);
    setHasChanges(true);
  };

  const handleEndChange = (date: Date | null) => {
    setEditEnd(date);
    setHasChanges(true);
  };

  const handleAccept = () => {
    if (isValid && editStart && editEnd) {
      onChange({ start: editStart, end: editEnd });
      setHasChanges(false);
    }
  };

  const handleCancel = () => {
    setEditStart(value.start);
    setEditEnd(value.end);
    setHasChanges(false);
  };

  const canAccept = isValid && editStart && editEnd;

  return (
    <div>
      <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
        <div>
          <DatePicker
            selected={editStart}
            onChange={handleStartChange}
            selectsStart
            startDate={editStart}
            endDate={editEnd}
            maxDate={maxDate}
            minDate={minDate}
            disabled={disabled}
            placeholderText={startPlaceholder}
            calendarStartDay={1}
            className={`form-control${sizeClass} ${!isValid ? "is-invalid" : ""}`}
            {...modeProps}
          />
        </div>
        <span>to</span>
        <div>
          <DatePicker
            selected={editEnd}
            onChange={handleEndChange}
            selectsEnd
            startDate={editStart}
            endDate={editEnd}
            minDate={editStart || minDate}
            maxDate={maxDate}
            disabled={disabled}
            placeholderText={endPlaceholder}
            calendarStartDay={1}
            className={`form-control${sizeClass} ${!isValid ? "is-invalid" : ""}`}
            {...modeProps}
          />
        </div>
        {hasChanges && (
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleCancel}
              disabled={disabled}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-success btn-sm"
              onClick={handleAccept}
              disabled={disabled || !canAccept}
            >
              Accept
            </button>
          </div>
        )}
      </div>
      {!isValid && (
        <div className="text-danger" style={{ fontSize: "0.875em" }}>
          End date must be after or equal to start date
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
